import React, { useEffect, useMemo, useState } from 'react';
import { FaBalanceScale, FaChartLine, FaExclamationTriangle, FaPiggyBank } from 'react-icons/fa';
import { newFinanceService } from '../../services/newFinance.service';
import { carryoverService } from '../../services/carryover.service';
import type { CurrencyTotals, FinanceRecord } from '../../types/newFinance';

const currencies: (keyof CurrencyTotals)[] = ['HUF', 'EUR', 'USD', 'KRW', 'GBP', 'CAD'];
const emptyTotals = (): CurrencyTotals => ({ HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 });
const add = (target: CurrencyTotals, source: CurrencyTotals) => {
  currencies.forEach((currency) => { target[currency] += source[currency] || 0; });
};
const formatAmount = (amount: number) => amount.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const FinanceOverview: React.FC = () => {
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [opening, setOpening] = useState<CurrencyTotals>(emptyTotals());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const currentYear = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const approved = await newFinanceService.getFinanceRecordsByStatus('approved');
        setRecords(approved);
        const carryover = await carryoverService.getAnnualCarryover(currentYear);
        if (carryover) setOpening(carryover.balances);
      } catch (error) {
        console.error('재정 요약 로딩 오류:', error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [currentYear, attempt]);

  const summary = useMemo(() => {
    const annualIncome = emptyTotals();
    const annualExpense = emptyTotals();
    const cumulativeIncome = emptyTotals();
    const cumulativeExpense = emptyTotals();
    const personalAdvance = emptyTotals();
    records.forEach((record) => {
      add(cumulativeIncome, record.incomeTotals);
      add(cumulativeExpense, record.expenseTotals);
      add(personalAdvance, record.personalAdvanceTotals || emptyTotals());
      if (record.date.startsWith(`${currentYear}-`) && record.date <= today) {
        add(annualIncome, record.incomeTotals);
        add(annualExpense, record.expenseTotals);
      }
    });
    const annualNet = Object.fromEntries(currencies.map((currency) => [currency, annualIncome[currency] - annualExpense[currency]])) as CurrencyTotals;
    const cumulativeNet = Object.fromEntries(currencies.map((currency) => [currency, cumulativeIncome[currency] - cumulativeExpense[currency]])) as CurrencyTotals;
    const bookBalance = Object.fromEntries(currencies.map((currency) => [currency, (opening[currency] || 0) + annualNet[currency]])) as CurrencyTotals;
    const available = Object.fromEntries(currencies.map((currency) => [currency, bookBalance[currency] - personalAdvance[currency]])) as CurrencyTotals;
    return { annualIncome, annualExpense, cumulativeIncome, cumulativeExpense, cumulativeNet, personalAdvance, bookBalance, available };
  }, [records, opening, currentYear, today]);

  if (loading) return <div className="py-4 text-center text-sm text-gray-500">재정 요약을 불러오는 중...</div>;
  if (loadError) return <div role="alert" className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">재정 요약을 불러오지 못했습니다. <button type="button" className="ml-2 font-semibold underline" onClick={() => setAttempt(value => value + 1)}>다시 시도</button></div>;

  return (
    <section aria-labelledby="finance-overview-title" className="mb-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="finance-overview-title" className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white"><FaBalanceScale className="text-blue-600" />재정 한눈에 보기</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">승인된 자료 기준 · 통화별 합계</p>
        </div>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{currentYear}년 현재</span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard icon={<FaChartLine />} title={`${currentYear}년 수입`} totals={summary.annualIncome} tone="green" />
        <SummaryCard icon={<FaExclamationTriangle />} title={`${currentYear}년 지출`} totals={summary.annualExpense} tone="red" />
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300"><FaPiggyBank />누적 순수입</div>
          <CurrencyRows totals={summary.cumulativeNet} />
          <p className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400">수입과 지출의 통화별 차액</p>
        </div>
        <SummaryCard icon={<FaBalanceScale />} title="장부상 현재 잔액" totals={summary.bookBalance} tone="blue" />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900/50">
          <div className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">개인 선지출 <span className="whitespace-nowrap font-normal">· 교회가 지급할 금액</span></div>
          <CurrencyRows totals={summary.personalAdvance} highlight="amber" />
        </div>
        <div className="rounded-lg bg-blue-50/70 p-3 dark:bg-blue-900/10">
          <div className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">선지출 차감 후 가용액</div>
          <CurrencyRows totals={summary.available} highlight="blue" />
        </div>
      </div>

      <p className="mt-3 text-[11px] leading-5 text-gray-500 dark:text-gray-400">잔액은 장부상 계산값입니다. 실제 현금 및 은행 잔고는 별도 대조가 필요합니다.</p>
    </section>
  );
};

const SummaryCard: React.FC<{ icon: React.ReactNode; title: string; totals: CurrencyTotals; tone: 'green' | 'red' | 'blue' }> = ({ icon, title, totals, tone }) => (
  <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
    <div className={`mb-2 flex items-center gap-2 text-sm font-medium ${toneClasses[tone]}`}>{icon}<span>{title}</span></div>
    <CurrencyRows totals={totals} />
  </div>
);

const CurrencyRows: React.FC<{ totals: CurrencyTotals; highlight?: 'amber' | 'blue' }> = ({ totals, highlight }) => {
  const nonZero = currencies.filter((currency) => totals[currency] !== 0);
  if (nonZero.length === 0) return <p className="text-sm font-semibold text-gray-400">0 <span className="text-xs font-medium">HUF</span></p>;
  return (
    <dl className="space-y-1">
      {nonZero.map((currency) => (
        <div key={currency} className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] font-medium tracking-wide text-gray-500 dark:text-gray-400">{currency}</dt>
          <dd className={`text-right text-sm font-semibold tabular-nums ${highlight === 'amber' ? 'text-amber-700 dark:text-amber-400' : highlight === 'blue' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}>{formatAmount(totals[currency])}</dd>
        </div>
      ))}
    </dl>
  );
};

const toneClasses: Record<'green' | 'red' | 'blue', string> = {
  green: 'text-emerald-700 dark:text-emerald-300',
  red: 'text-rose-700 dark:text-rose-300',
  blue: 'text-blue-700 dark:text-blue-300',
};
